/**
 * Webhooks Settings tab. Mounted inside SettingsPage when the user clicks
 * the "Integrations" tab. Enterprise-only (Professional+ in the future).
 */
import React, { useEffect, useState } from "react";
import { Plus, X, Loader2, Copy, CheckCircle2, AlertCircle, Trash2, Play, Power, ChevronDown, ChevronRight } from "lucide-react";
import api, { formatApiError } from "../lib/api";

export default function WebhooksTab() {
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", events: [] });
  const [busy, setBusy] = useState(null);
  const [justCreatedSecret, setJustCreatedSecret] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [deliveries, setDeliveries] = useState({});
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try { const { data } = await api.get("/api/webhooks"); setData(data); }
    catch (e) { setError(formatApiError(e)); setData({ items: [], available_events: [] }); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.url.startsWith("http") || form.events.length === 0 || !form.name) {
      alert("Enter a name, valid URL and at least one event."); return;
    }
    setBusy("create");
    try {
      const { data } = await api.post("/api/webhooks", form);
      setJustCreatedSecret({ name: data.name, secret: data.secret });
      setShowAdd(false); setForm({ name: "", url: "", events: [] });
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this webhook endpoint?")) return;
    setBusy("del-" + id);
    try { await api.delete(`/api/webhooks/${id}`); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const toggle = async (id) => {
    setBusy("tog-" + id);
    try { await api.post(`/api/webhooks/${id}/toggle`); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const test = async (id) => {
    setBusy("test-" + id);
    try {
      const { data } = await api.post(`/api/webhooks/${id}/test`);
      alert(data.ok ? `Test sent — HTTP ${data.delivery?.status_code} in ${data.delivery?.response_time_ms ?? "—"}ms` : `Test failed — ${data.delivery?.error || data.delivery?.status_code || "no response"}`);
      await loadDeliveries(id);
      await load();
    } catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const loadDeliveries = async (id) => {
    try {
      const { data } = await api.get(`/api/webhooks/${id}/deliveries?limit=20`);
      setDeliveries((p) => ({ ...p, [id]: data.items }));
    } catch { /* ignore */ }
  };

  const toggleExpand = async (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
    if (!deliveries[id]) await loadDeliveries(id);
  };

  if (!data) return <div className="card2 p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate2" /></div>;
  if (error) return <div className="card2 p-8 text-center text-slate2" data-testid="webhooks-error">{error}</div>;

  return (
    <div className="space-y-5 max-w-3xl" data-testid="webhooks-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h3 className="t-h2">Webhooks</h3>
          <p className="t-body-sm text-slate2 mt-1">Receive real-time events from Compliche in your own systems.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary" data-testid="webhook-add"><Plus size={16}/>Add endpoint</button>
      </div>

      {/* Newly-created secret callout */}
      {justCreatedSecret && (
        <div className="card2 p-5 bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-700 mt-0.5"/>
            <div className="flex-1">
              <div className="font-semibold text-amber-900">Save this secret now</div>
              <p className="t-body-sm text-amber-800 mt-1">This is the only time we'll show the signing secret. Store it in your application's secret manager — you'll use it to verify webhook signatures.</p>
              <code className="block mt-2 px-3 py-2 bg-white border border-amber-200 rounded font-mono text-xs break-all">{justCreatedSecret.secret}</code>
              <button onClick={() => { navigator.clipboard?.writeText(justCreatedSecret.secret); alert("Copied"); }} className="btn btn-secondary btn-sm mt-3"><Copy size={14}/>Copy</button>
              <button onClick={() => setJustCreatedSecret(null)} className="btn btn-ghost btn-sm mt-3 ml-2">I've saved it</button>
            </div>
          </div>
        </div>
      )}

      {/* Endpoints */}
      {data.items.length === 0 ? (
        <div className="card2 p-10 text-center text-slate2">No webhook endpoints registered yet.</div>
      ) : data.items.map((w) => (
        <div key={w.id} className="card2" data-testid={`webhook-${w.id}`}>
          <div className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold">{w.name}</span>
                  <span className={`badge2 ${w.status === "active" ? "badge-verified" : w.status === "failing" ? "badge-warning" : "badge-pending"}`}>{w.status}</span>
                </div>
                <div className="t-mono text-xs text-slate2 break-all">{w.url}</div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {w.events.map((e) => <span key={e} className="text-xs px-2 py-0.5 bg-bg2 rounded-full">{e}</span>)}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => test(w.id)} disabled={busy === "test-" + w.id} className="btn btn-secondary btn-sm" data-testid={`webhook-test-${w.id}`}>{busy === "test-" + w.id ? <Loader2 size={12} className="animate-spin"/> : <Play size={12}/>}Test</button>
                <button onClick={() => toggle(w.id)} disabled={busy === "tog-" + w.id} className="btn btn-ghost btn-sm" title={w.status === "disabled" ? "Enable" : "Disable"}><Power size={14}/></button>
                <button onClick={() => remove(w.id)} disabled={busy === "del-" + w.id} className="btn btn-ghost btn-sm text-rose-600"><Trash2 size={14}/></button>
              </div>
            </div>
            <button onClick={() => toggleExpand(w.id)} className="mt-3 t-body-sm text-indigo2 flex items-center gap-1 hover:underline">
              {expanded[w.id] ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}Delivery log
            </button>
          </div>
          {expanded[w.id] && (
            <div className="border-t border-border2 px-5 py-3 bg-bg2/30">
              {!deliveries[w.id] ? <Loader2 size={14} className="animate-spin"/> :
               deliveries[w.id].length === 0 ? <div className="t-caption text-slate2">No deliveries yet.</div> :
               <table className="w-full t-body-sm">
                 <thead className="text-slate2 text-left text-xs"><tr><th className="py-2">Time</th><th>Event</th><th>Status</th><th>Time(ms)</th><th>Attempt</th></tr></thead>
                 <tbody>{deliveries[w.id].map((d) => (
                   <tr key={d.id} className="border-t border-border2/50">
                     <td className="py-1.5 t-mono text-xs">{d.created_at?.slice(11, 19)}</td>
                     <td>{d.event}</td>
                     <td>{d.ok ? <CheckCircle2 size={12} className="text-emerald2 inline"/> : <AlertCircle size={12} className="text-rose-500 inline"/>} {d.status_code || "—"}</td>
                     <td className="t-mono">{d.response_time_ms ?? "—"}</td>
                     <td>{d.attempt}</td>
                   </tr>
                 ))}</tbody>
               </table>
              }
            </div>
          )}
        </div>
      ))}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="card2 max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4"><h3 className="t-h3">New webhook endpoint</h3><button onClick={() => setShowAdd(false)}><X size={18}/></button></div>
            <div className="space-y-3">
              <input className="input2 w-full" placeholder="Name (e.g. 'Slack alerts')" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} data-testid="webhook-name" />
              <input className="input2 w-full" placeholder="https://example.com/webhooks/compliche" value={form.url} onChange={(e)=>setForm({...form, url:e.target.value})} data-testid="webhook-url" />
              <div>
                <div className="t-label mb-2">Subscribed events</div>
                <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                  {data.available_events.map((e) => (
                    <label key={e} className="flex items-center gap-2 cursor-pointer t-body-sm">
                      <input type="checkbox" checked={form.events.includes(e)} onChange={(ev) => setForm({...form, events: ev.target.checked ? [...form.events, e] : form.events.filter(x => x !== e)})} />
                      <code className="text-xs">{e}</code>
                    </label>
                  ))}
                </div>
              </div>
              <button onClick={create} disabled={busy === "create"} className="btn btn-primary w-full" data-testid="webhook-create">{busy === "create" ? <Loader2 size={14} className="animate-spin"/> : "Create endpoint"}</button>
              <p className="t-caption text-center">We'll sign every request with HMAC-SHA256 in the <code>X-Compliche-Signature</code> header.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
